import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Input, Modal, Select } from "antd";
import {
  BellRing,
  Inbox,
  RefreshCw,
  Send,
  Smartphone,
  Users,
} from "lucide-react";
import { RiSendPlaneLine } from "react-icons/ri";
import {
  getNotificationCases,
  previewNotificationAudience,
  sendNotification,
} from "../../../../Service/RM";
import { showToast } from "../../../../reusableUI/toaster";
import DutyReminderMonitor from "./dutyReminderMonitor";

const { Option } = Select;

const SendNotificationPanel = () => {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [selectedCaseId, setSelectedCaseId] = useState("");
  const [sending, setSending] = useState(false);
  const [cases, setCases] = useState([]);
  const [casesLoading, setCasesLoading] = useState(true);
  const [casesError, setCasesError] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const loadCases = useCallback(async () => {
    setCasesLoading(true);
    setCasesError("");
    const result = await getNotificationCases();

    if (result?.error === false && Array.isArray(result?.data)) {
      setCases(result.data);
    } else {
      setCases([]);
      setCasesError(result?.message || "Unable to load notification cases");
    }
    setCasesLoading(false);
  }, []);

  useEffect(() => {
    loadCases();
  }, [loadCases]);

  useEffect(() => {
    if (!selectedCaseId) {
      setPreview(null);
      setPreviewError("");
      return undefined;
    }

    let isActive = true;
    setPreview(null);
    setPreviewError("");
    setPreviewLoading(true);

    previewNotificationAudience(selectedCaseId).then((result) => {
      if (!isActive) return;

      if (result?.error === false && result?.data) {
        setPreview(result.data);
      } else {
        setPreviewError(
          result?.message || "Unable to calculate the notification audience",
        );
      }
      setPreviewLoading(false);
    });

    return () => {
      isActive = false;
    };
  }, [selectedCaseId]);

  const handleCaseChange = (caseId) => {
    const selectedCase = cases.find((item) => item.id === caseId);
    setSelectedCaseId(caseId);
    setTitle(selectedCase?.title || "");
    setMessage(selectedCase?.message || "");
  };

  const handleSend = async () => {
    setSending(true);

    try {
      const result = await sendNotification({
        caseId: selectedCaseId,
        title: title.trim(),
        message: message.trim(),
      });

      if (result?.error === false) {
        showToast(result.message, "Success");
        setConfirmOpen(false);
        setSelectedCaseId("");
        setTitle("");
        setMessage("");
        setPreview(null);
      } else {
        showToast(result?.message || "Messages not delivered", "Error");
      }
    } catch (error) {
      showToast("Messages not delivered", "Error");
    } finally {
      setSending(false);
    }
  };

  const trimmedTitle = title.trim();
  const trimmedMessage = message.trim();
  const canReview =
    Boolean(selectedCaseId && trimmedTitle && trimmedMessage) &&
    Boolean(preview?.matchedCount) &&
    !previewLoading &&
    !previewError &&
    !sending;

  return (
    <div className="mx-auto max-w-5xl rounded-xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
          <Send size={19} strokeWidth={1.9} />
        </div>
        <div>
          <h2 className="text-base font-semibold text-slate-900">
            Send notification
          </h2>
          <p className="mt-1 text-xs text-slate-500">
            Select a user group, confirm the audience and review the message.
          </p>
        </div>
      </div>

      {casesError ? (
        <Alert
          type="error"
          showIcon
          message="Unable to load notification cases"
          description={casesError}
          action={
            <Button
              size="small"
              icon={<RefreshCw size={14} />}
              onClick={loadCases}
            >
              Retry
            </Button>
          }
          className="mb-5"
        />
      ) : null}

      <div className="grid gap-7 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="space-y-5">
          <div>
            <label className="mb-2 block text-xs font-medium text-slate-700">
              Notification case
            </label>
            <Select
              value={selectedCaseId || undefined}
              onChange={handleCaseChange}
              placeholder="Select case"
              className="w-full"
              size="large"
              loading={casesLoading}
              disabled={casesLoading || Boolean(casesError)}
              showSearch
              optionFilterProp="children"
            >
              {cases.map((option) => (
                <Option key={option.id} value={option.id}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </div>

          <div>
            <label className="mb-2 block text-xs font-medium text-slate-700">
              Title
            </label>
            <Input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Enter title"
              size="large"
              maxLength={100}
              showCount
            />
          </div>

          <div>
            <label className="mb-2 block text-xs font-medium text-slate-700">
              Message
            </label>
            <Input.TextArea
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              placeholder="Enter message"
              rows={7}
              maxLength={1500}
              showCount
            />
          </div>

          <div className="flex justify-end pt-2">
            <Button
              type="primary"
              icon={<RiSendPlaneLine />}
              onClick={() => setConfirmOpen(true)}
              disabled={!canReview}
              size="large"
            >
              Review and send
            </Button>
          </div>
        </div>

        <aside className="rounded-xl border border-slate-200 bg-slate-50 p-5">
          <h3 className="text-sm font-semibold text-slate-900">
            Audience preview
          </h3>
          <p className="mt-1 text-xs leading-5 text-slate-500">
            Counts are calculated from the current user records.
          </p>

          {previewLoading ? (
            <div className="mt-6 flex items-center gap-2 text-sm text-slate-500">
              <RefreshCw size={15} className="animate-spin" />
              Calculating audience...
            </div>
          ) : previewError ? (
            <Alert
              type="error"
              showIcon
              message={previewError}
              className="mt-5"
            />
          ) : preview ? (
            <div className="mt-5 space-y-3">
              <div className="flex items-center justify-between rounded-lg bg-white px-3 py-3">
                <span className="flex items-center gap-2 text-xs text-slate-600">
                  <Users size={15} className="text-blue-600" />
                  Matched users
                </span>
                <strong className="text-sm text-slate-900">
                  {preview.matchedCount}
                </strong>
              </div>
              <div className="flex items-center justify-between rounded-lg bg-white px-3 py-3">
                <span className="flex items-center gap-2 text-xs text-slate-600">
                  <Smartphone size={15} className="text-emerald-600" />
                  Push eligible
                </span>
                <strong className="text-sm text-slate-900">
                  {preview.pushEligibleCount}
                </strong>
              </div>
              <div className="flex items-center justify-between rounded-lg bg-white px-3 py-3">
                <span className="flex items-center gap-2 text-xs text-slate-600">
                  <Inbox size={15} className="text-amber-600" />
                  In-app only
                </span>
                <strong className="text-sm text-slate-900">
                  {preview.inAppOnlyCount}
                </strong>
              </div>

              {preview.matchedCount === 0 ? (
                <Alert
                  type="warning"
                  showIcon
                  message="No users currently match this case"
                />
              ) : null}

              <p className="pt-1 text-[11px] leading-4 text-slate-500">
                Push eligibility follows the current signed-in and valid-token
                rules. Every matched user receives an in-app notification.
              </p>
            </div>
          ) : (
            <div className="mt-6 rounded-lg border border-dashed border-slate-300 px-4 py-6 text-center text-xs leading-5 text-slate-500">
              Select a notification case to preview the audience.
            </div>
          )}
        </aside>
      </div>

      <Modal
        open={confirmOpen}
        title="Confirm notification"
        okText="Send notification"
        cancelText="Go back"
        confirmLoading={sending}
        onOk={handleSend}
        onCancel={() => !sending && setConfirmOpen(false)}
        okButtonProps={{ disabled: !canReview && !sending }}
        maskClosable={!sending}
        closable={!sending}
      >
        <div className="space-y-4 pt-2 text-sm text-slate-600">
          <p>
            You are sending <strong className="text-slate-900">{title}</strong>{" "}
            to <strong className="text-slate-900">{preview?.matchedCount || 0}</strong>{" "}
            matched users.
          </p>
          <div className="rounded-lg bg-slate-50 px-4 py-3 text-xs leading-5">
            <p>{preview?.pushEligibleCount || 0} are currently push eligible.</p>
            <p>{preview?.inAppOnlyCount || 0} will receive it in the app only.</p>
          </div>
          <p className="text-xs text-slate-500">
            The server will validate and recalculate the audience before sending.
          </p>
        </div>
      </Modal>
    </div>
  );
};

const NotificationCenter = () => {
  const [activeTab, setActiveTab] = useState("monitor");

  return (
    <div className="min-h-[calc(100vh-72px)] bg-[#F3F6F5] px-3 py-5 sm:px-5 lg:px-6">
      <div className="mx-auto w-full max-w-[1680px]">
        <div className="mb-5">
          <h1 className="text-2xl font-semibold tracking-tight text-[#09244A] sm:text-[30px]">
            Notification Center
          </h1>
          <p className="mt-2 max-w-3xl text-sm text-slate-500">
            Send user updates or monitor scheduled duty reminders across active
            bookings.
          </p>
        </div>

        <div className="mb-5 flex gap-7 border-b border-slate-200">
          <button
            type="button"
            onClick={() => setActiveTab("send")}
            className={`flex items-center gap-2 border-b-2 px-1 pb-3 text-sm font-medium transition-colors ${
              activeTab === "send"
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            <Send size={16} strokeWidth={1.9} />
            Send Notification
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("monitor")}
            className={`flex items-center gap-2 border-b-2 px-1 pb-3 text-sm font-medium transition-colors ${
              activeTab === "monitor"
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            <BellRing size={16} strokeWidth={1.9} />
            Duty Reminder Monitor
          </button>
        </div>

        {activeTab === "send" ? (
          <SendNotificationPanel />
        ) : (
          <DutyReminderMonitor />
        )}
      </div>
    </div>
  );
};

export default NotificationCenter;
